import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { updateCampaignCommercials } from '../../api_handlers/invoice';
import { Alert, money } from './kit';
import {
  COMMERCIAL_FIELDS,
  commercialsPayload,
  margin,
  spendStatus,
  validateCommercials,
} from './lib/commercials';
import { committedOf, projectChecks } from './lib/projectChecks';

/**
 * The project's commercial header (spec §4.1) — the block the Project Details
 * sheet carries above its creator table.
 *
 * Read-only summary until someone chooses to edit, because these figures are
 * agreed once and then referred to often. Margin is shown but never stored.
 *
 * Visible only where the API returns the fields, which is gated to Invoice
 * Business and Finance. The fields do not exist yet, so today this renders as
 * an empty state inviting the first entry rather than a wall of blanks.
 */

export const COMMERCIALS_CSS = `
.pc-card { border: 1px solid var(--line); border-radius: var(--radius); background: var(--surface); padding: 16px 18px; margin-bottom: 16px; }
.pc-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.pc-title { font-size: 13px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: var(--ink-4); margin: 0; }
.pc-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 14px 20px; margin-top: 14px; }
.pc-item__label { font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--ink-4); margin: 0 0 3px; }
.pc-item__value { font-size: 13.5px; font-weight: 600; color: var(--ink); margin: 0; overflow-wrap: anywhere; }
.pc-item__value--muted { color: var(--ink-4); font-weight: 500; }
.pc-money { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 14px; }
.pc-fig { flex: 1 1 150px; border: 1px solid var(--line); border-radius: var(--radius-sm); padding: 10px 12px; }
.pc-fig__label { font-size: 10.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-4); margin: 0 0 4px; }
.pc-fig__value { font-size: 16px; font-weight: 800; color: var(--ink); }
.pc-fig__value--neg { color: #B42318; }
.pc-spend { margin-top: 14px; }
.pc-spend__row { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; font-size: 12.5px; color: var(--ink-3); }
.pc-spend__bar { height: 6px; border-radius: 999px; background: var(--canvas); margin-top: 7px; overflow: hidden; }
.pc-spend__fill { height: 100%; border-radius: 999px; background: var(--ink); transition: width 240ms ease; }
.pc-spend__fill--over { background: #B45309; }
.pc-spend__over { color: #92400E; font-weight: 700; }
.pc-checks { list-style: none; margin: 14px 0 0; padding: 0; display: grid; gap: 7px; }
.pc-check { display: flex; align-items: flex-start; gap: 9px; font-size: 12.5px; line-height: 1.5; border-radius: var(--radius-sm); padding: 9px 11px; }
.pc-check--warn { color: #92400E; background: #FFFBEB; border: 1px solid #FDE68A; }
.pc-check--info { color: var(--ink-3); background: var(--canvas); border: 1px solid var(--line); }
.pc-check__dot { flex: 0 0 auto; width: 7px; height: 7px; border-radius: 50%; margin-top: 6px; }
.pc-check--warn .pc-check__dot { background: #B45309; }
.pc-check--info .pc-check__dot { background: var(--ink-4); }
.pc-ok { font-size: 12.5px; color: #027A48; margin: 14px 0 0; }
.pc-form { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; margin-top: 14px; }
.pc-form__wide { grid-column: 1 / -1; }
.pc-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }
@media (prefers-reduced-motion: reduce) { .pc-spend__fill { transition: none; } }
`;

const TEXT_FIELDS = [
  { key: 'project_code', label: 'Project code', placeholder: 'TVS-SWE-TVS-01919' },
  { key: 'client_name', label: 'Client name' },
  { key: 'client_gstin', label: 'Client GSTIN', placeholder: '33AAIFF6389B1ZG' },
  { key: 'team_lead', label: 'Team lead' },
];

const MONEY_FIELDS = [
  { key: 'client_cost', label: 'Client cost' },
  { key: 'internal_cost', label: 'Internal cost' },
  { key: 'management_fees', label: 'Management fees' },
];

function formFrom(campaign) {
  const source = campaign || {};
  return COMMERCIAL_FIELDS.reduce(
    (form, field) => ({ ...form, [field]: source[field] === null || source[field] === undefined ? '' : String(source[field]) }),
    {},
  );
}

function Figure({ label, value, negative }) {
  return (
    <div className="pc-fig">
      <p className="pc-fig__label">{label}</p>
      <div className={`pc-fig__value iv-num${negative ? ' pc-fig__value--neg' : ''}`}>{value}</div>
    </div>
  );
}

function Item({ label, value }) {
  return (
    <div className="pc-item">
      <p className="pc-item__label">{label}</p>
      <p className={`pc-item__value${value ? '' : ' pc-item__value--muted'}`}>{value || 'Not set'}</p>
    </div>
  );
}

export default function ProjectCommercials({ campaign, spend, lines, invoices, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => formFrom(campaign));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(formFrom(campaign));
    setEditing(false);
    setErrors({});
  }, [campaign]);

  if (!campaign) return null;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    const found = validateCommercials(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    try {
      // The response is the saved block, not an echo of the request: money
      // comes back as strings and the GSTIN has been uppercased and stripped
      // server-side. Hand that up, so the card shows what was stored rather
      // than what was typed.
      const saved = await updateCampaignCommercials(campaign.id, commercialsPayload(form));
      toast.success('Project details saved');
      setEditing(false);
      if (onSaved) onSaved(saved);
    } catch (e) {
      setErrors({ form: e.message });
    } finally {
      setSaving(false);
    }
  };

  const net = margin(campaign);
  const status = spendStatus(spend, campaign.internal_cost);
  const checks = projectChecks({ campaign, lines, invoices });
  const committed = committedOf(lines);

  if (editing) {
    return (
      <section className="pc-card" aria-label="Project details">
        <div className="pc-head">
          <p className="pc-title">Project details</p>
        </div>
        {errors.form ? <Alert>{errors.form}</Alert> : null}
        <div className="pc-form">
          {TEXT_FIELDS.map((field) => (
            <label className="iv-field" key={field.key}>
              <span className="iv-label">{field.label}</span>
              <input
                className="iv-input"
                value={form[field.key] || ''}
                placeholder={field.placeholder}
                onChange={set(field.key)}
              />
              {errors[field.key] ? <span className="iv-error">{errors[field.key]}</span> : null}
            </label>
          ))}
          {MONEY_FIELDS.map((field) => (
            <label className="iv-field" key={field.key}>
              <span className="iv-label">{field.label} (₹)</span>
              <input
                className="iv-input iv-num"
                type="number"
                min="0"
                value={form[field.key] || ''}
                onChange={set(field.key)}
              />
              {errors[field.key] ? <span className="iv-error">{errors[field.key]}</span> : null}
            </label>
          ))}
          <label className="iv-field pc-form__wide">
            <span className="iv-label">Billing address</span>
            <input
              className="iv-input"
              value={form.client_billing_address || ''}
              onChange={set('client_billing_address')}
            />
          </label>
          <label className="iv-field pc-form__wide">
            <span className="iv-label">Payment terms</span>
            <input
              className="iv-input"
              value={form.payment_terms || ''}
              placeholder="Within 30 days from raising the tax invoice, post campaign completion"
              onChange={set('payment_terms')}
            />
          </label>
        </div>
        <div className="pc-actions">
          <button type="button" className="iv-btn iv-btn--ghost" onClick={() => setEditing(false)}>
            Cancel
          </button>
          <button type="button" className="iv-btn iv-btn--primary" onClick={save} disabled={saving}>
            {saving ? <span className="iv-spin" /> : null}
            Save details
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="pc-card" aria-label="Project details">
      <div className="pc-head">
        <p className="pc-title">Project details</p>
        <button type="button" className="iv-btn iv-btn--ghost iv-btn--sm" onClick={() => setEditing(true)}>
          Edit
        </button>
      </div>

      <div className="pc-grid">
        <Item label="Project code" value={campaign.project_code} />
        <Item label="Client" value={campaign.client_name} />
        <Item label="GSTIN" value={campaign.client_gstin} />
        <Item label="Team lead" value={campaign.team_lead} />
        <Item label="Payment terms" value={campaign.payment_terms} />
      </div>

      <div className="pc-money">
        <Figure label="Client cost" value={money(campaign.client_cost)} />
        <Figure label="Internal cost" value={money(campaign.internal_cost)} />
        <Figure label="Management fees" value={money(campaign.management_fees)} />
        <Figure label="Net margin" value={money(net)} negative={net < 0} />
      </div>

      {committed > 0 ? (
        <p className="pc-spend__row" style={{ marginTop: 12 }}>
          <span>
            Roster commits <strong className="iv-num">{money(committed)}</strong>
            {campaign.internal_cost ? ` of ${money(campaign.internal_cost)} internal cost` : ''}
          </span>
        </p>
      ) : null}

      {status ? (
        <div className="pc-spend">
          <div className="pc-spend__row">
            <span>
              <strong className="iv-num">{money(status.spend)}</strong> invoiced of{' '}
              <strong className="iv-num">{money(status.budget)}</strong> internal cost
            </span>
            {status.over ? (
              <span className="pc-spend__over">Over by {money(status.overBy)}</span>
            ) : (
              <span>{status.percent}%</span>
            )}
          </div>
          <div
            className="pc-spend__bar"
            role="progressbar"
            aria-valuenow={status.spend}
            aria-valuemin={0}
            aria-valuemax={status.budget}
          >
            <div
              className={`pc-spend__fill${status.over ? ' pc-spend__fill--over' : ''}`}
              style={{ width: `${status.percent}%` }}
            />
          </div>
        </div>
      ) : null}

      {checks.length > 0 ? (
        <ul className="pc-checks">
          {checks.map((check) => (
            <li key={check.id} className={`pc-check pc-check--${check.severity}`}>
              <span className={`pc-check__dot`} aria-hidden="true" />
              <span>{check.message}</span>
            </li>
          ))}
        </ul>
      ) : campaign.internal_cost ? (
        <p className="pc-ok">Costs are within the agreed figures.</p>
      ) : null}
    </section>
  );
}
